import React from 'react';
import * as consts from 'config/consts';

export type ProductTypes = typeof consts.productTypes[keyof typeof consts.productTypes];

const foreignStockTypes = [
  consts.productTypes.外国株式,
  consts.productTypes['外国ETF・ETN'],
  consts.productTypes.外国REIT,
  consts.productTypes.外国債券,
  consts.productTypes.外国EB,
] as number[];
export const isForeignStockTypes = (type: number) => {
  return foreignStockTypes.includes(type);
};

// memo: 銘柄詳細に遷移させない商品種別
const notExistDetailTypes = [
  consts.productTypes.外国株式,
  consts.productTypes['外国ETF・ETN'],
  consts.productTypes.外国REIT,
  consts.productTypes.国内債券,
  consts.productTypes.外国債券,
  consts.productTypes.国内EB,
  consts.productTypes.外国EB,
  consts.productTypes.不動産,
  consts.productTypes.未上場株式,
  consts.productTypes.ロボアド,
  consts.productTypes.預金_MRF,
  consts.productTypes.その他,
] as number[];
export const isNotExistDetailTypes = (type: number) => {
  return notExistDetailTypes.includes(type);
};

export const productTypesToUnit = (productTypes: number): string => {
  switch (productTypes) {
    case consts.productTypes.国内株式:
    case consts.productTypes.外国株式:
    case consts.productTypes.未上場株式:
    case consts.productTypes.ロボアド:
      return '株';
    case consts.productTypes.投資信託:
    case consts.productTypes['国内ETF・ETN']:
    case consts.productTypes['外国ETF・ETN']:
    case consts.productTypes.国内REIT:
    case consts.productTypes.外国REIT:
      return '口';
    default:
      return '';
  }
};

// memo: 数量項目を非表示にする商品種別
const qtyHiddenProductTypeList = [
  consts.productTypes.国内債券,
  consts.productTypes.外国債券,
  consts.productTypes.国内EB,
  consts.productTypes.外国EB,
  consts.productTypes.不動産,
  consts.productTypes['預金_MRF'],
  consts.productTypes.ロボアド,
  consts.productTypes.その他,
] as number[];
export const isQtyHiddenProductType = (productTypes: number): boolean => {
  return qtyHiddenProductTypeList.includes(productTypes);
};

export const productTypesToString = (
  productTypes: number,
  cashMargin: number
): string => {
  if (cashMargin === consts.CashMargin.信用) return '信用取引';
  switch (productTypes) {
    case consts.productTypes.国内株式:
      return '国内株式';
    case consts.productTypes.外国株式:
      return '外国株式';
    case consts.productTypes.投資信託:
      return '投資信託';
    case consts.productTypes.ロボアド:
      return 'ロボアド';
    case consts.productTypes.預金_MRF:
      return '預金・MRF';
    case consts.productTypes.未上場株式:
      return '未上場株式';
    case consts.productTypes['国内ETF・ETN']:
      return '国内ETF・ETN';
    case consts.productTypes['外国ETF・ETN']:
      return '外国ETF・ETN';
    case consts.productTypes.国内REIT:
      return '国内REIT';
    case consts.productTypes.外国REIT:
      return '外国REIT';
    case consts.productTypes.国内債券:
      return '国内債券';
    case consts.productTypes.外国債券:
      return '外国債券';
    case consts.productTypes.国内EB:
      return '国内EB';
    case consts.productTypes.外国EB:
      return '外国EB';
    case consts.productTypes.不動産:
      return '不動産';
    case consts.productTypes.その他:
      return 'その他';
    default:
      return '';
  }
};

export const productTypesToStringLabel = (
  productTypes: number,
  cashMargin: number
): string => {
  if (cashMargin === consts.CashMargin.信用) return '信用取引';
  switch (productTypes) {
    case consts.productTypes.国内株式:
      return '国内株';
    case consts.productTypes.外国株式:
      return '外国株';
    case consts.productTypes.投資信託:
      return '投信';
    case consts.productTypes.ロボアド:
      return 'ロボアド';
    case consts.productTypes.預金_MRF:
      return '預金';
    case consts.productTypes.未上場株式:
      return '未上場株式';
    case consts.productTypes['国内ETF・ETN']:
      return '国内ETF・ETN';
    case consts.productTypes['外国ETF・ETN']:
      return '外国ETF・ETN';
    case consts.productTypes.国内REIT:
      return '国内REIT';
    case consts.productTypes.外国REIT:
      return '外国REIT';
    case consts.productTypes.国内債券:
      return '国内債券';
    case consts.productTypes.外国債券:
      return '外国債券';
    case consts.productTypes.国内EB:
      return '国内EB';
    case consts.productTypes.外国EB:
      return '外国EB';
    case consts.productTypes.不動産:
      return '不動産';
    case consts.productTypes.その他:
      return 'その他';
    default:
      return '';
  }
};

export const productTypesToColor = (
  productTypes: number,
  cashMargin: number
): string => {
  if (cashMargin === consts.CashMargin.信用) return 'bg-dot-Margin';
  switch (productTypes) {
    case consts.productTypes.国内株式:
      return 'bg-dot-Stock';
    case consts.productTypes.外国株式:
      return 'bg-dot-ForeignStock';
    case consts.productTypes.投資信託:
      return 'bg-dot-InvestmentTrust';
    case consts.productTypes.ロボアド:
      return 'bg-dot-Robo';
    case consts.productTypes.預金_MRF:
      return 'bg-dot-Cash';
    case consts.productTypes.未上場株式:
      return 'bg-dot-UnlistedStocks';
    case consts.productTypes['国内ETF・ETN']:
      return 'bg-dot-EtfEtn';
    case consts.productTypes['外国ETF・ETN']:
      return 'bg-dot-ForeignEtfEtn';
    case consts.productTypes.国内REIT:
      return 'bg-dot-Reit';
    case consts.productTypes.外国REIT:
      return 'bg-dot-ForeignReit';
    case consts.productTypes.国内債券:
      return 'bg-dot-Bond';
    case consts.productTypes.外国債券:
      return 'bg-dot-ForeignBond';
    case consts.productTypes.国内EB:
      return 'bg-dot-Eb';
    case consts.productTypes.外国EB:
      return 'bg-dot-ForeignEb';
    case consts.productTypes.不動産:
      return 'bg-dot-RealEstate';
    case consts.productTypes.その他:
      return 'bg-dot-Other';
    default:
      return '';
  }
};

export const ProductTypesLabel = ({
  productType,
  cashMargin,
  className = '',
}: {
  productType: number;
  cashMargin: number;
  className?: string;
}) => {
  const str = productTypesToStringLabel(productType, cashMargin);
  const labelColor = productTypesToColor(productType, cashMargin);
  return (
    <>
      <div
        className={`text-white text-1.5xs px-3 text-center justify-center flex items-center h-4 rounded-full ${className} ${labelColor}`}>
        {str}
      </div>
    </>
  );
};
